import { useState } from "react";

import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import createIcon from "@impero-com/common/img/create-icon.svg";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { actionMenuStatus, NoContent } from "@impero-com/ui";

import { WebRoute } from "routes";
import { useAvailableActions } from "utils/hooks/useAvailableActions";

import { TagsListApplicationBar } from "../../applicationBar";
import { useControlProgramTagsSuspenseQuery } from "../queries/useControlProgramTagsQuery";

import { ControlProgramTagsList } from "./ControlProgramTagsList";
import { CreateControlProgramTagCategoryDrawer } from "./CreateControlProgramTagCategoryDrawer";
import { EditControlProgramTagCategoryDrawer } from "./EditControlProgramTagCategoryDrawer";
import { CategoryOverview } from "./useControlProgramTagsListTable";

export function ControlProgramTagsListPage() {
    const { t } = useTranslation();
    const { userTag } = useAvailableActions();
    const userHasAccess = userTag.create; // its the same permission for control program tags
    const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false);
    const actions = [
        {
            id: "menuitem-1",
            label: t("TagsList_Action_CreateNewTagCategory"),
            icon: { svg: createIcon },
            link: { to: WebRoute.TAGS_CONTROL_PROGRAM },
            status: actionMenuStatus(
                userHasAccess,
                t("ControlProgramTag_Create_Forbidden")
            ),
            onClick: () => {
                setIsCreateDrawerOpen(true);
            },
        },
    ];

    return (
        <TagsListApplicationBar
            currentTabId="tab-control-program-tags"
            actions={actions}
        >
            <ControlProgramTagListContent
                userHasAccess={userHasAccess}
                isCreateDrawerOpen={isCreateDrawerOpen}
                setIsCreateDrawerOpen={setIsCreateDrawerOpen}
            />
        </TagsListApplicationBar>
    );
}

interface ControlProgramTagListContentProps {
    userHasAccess: boolean;
    isCreateDrawerOpen: boolean;
    setIsCreateDrawerOpen: (isOpen: boolean) => void;
}

function ControlProgramTagListContent({
    userHasAccess,
    isCreateDrawerOpen,
    setIsCreateDrawerOpen,
}: ControlProgramTagListContentProps) {
    const { t } = useTranslation();
    const { data } = useControlProgramTagsSuspenseQuery();
    const [selectedCategory, setSelectedCategory] =
        useState<CategoryOverview | null>(null);
    const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
    const hasCpTags = data.tagCategories.length > 0;

    if (!userHasAccess) {
        return <NoAccessCard />;
    }

    return (
        <>
            {hasCpTags ? (
                <ControlProgramTagsList
                    tags={data.tags}
                    tagCategories={data.tagCategories}
                    isDrawerOpen={isEditDrawerOpen}
                    setIsDrawerOpen={setIsEditDrawerOpen}
                    setSelectedCategory={setSelectedCategory}
                />
            ) : (
                <NoContent
                    header={t("ProgramTagsList_NoContent_Header")}
                    description={t("ProgramTagsList_NoContent_Description")}
                    actionCreation={{
                        type: "BUTTON",
                        label: t("ProgramTagsList_NoContent_Action"),
                        onClick: () => setIsCreateDrawerOpen(true),
                    }}
                />
            )}
            <CreateControlProgramTagCategoryDrawer
                isOpen={isCreateDrawerOpen}
                setIsOpen={setIsCreateDrawerOpen}
                tagCategories={data.tagCategories}
            />
            <EditControlProgramTagCategoryDrawer
                isOpen={isEditDrawerOpen}
                setIsOpen={setIsEditDrawerOpen}
                category={selectedCategory}
                tags={data.tags}
                tagCategories={data.tagCategories}
            />
        </>
    );
}
